import { useId } from "react";

import { buildFeeAdjustmentDraftDisplay, formatFeeAdjustmentAmount } from "../../fee-adjustment/service";
import { FeeAdjustmentServiceTable } from "../../fee-adjustment/ui/FeeAdjustmentServiceTable";
import {
  feeAdjustmentSpecUnit,
  canEditFeeAdjustmentBillingCount,
  previewStructuredFeeAdjustment,
  type StructuredFeeAdjustmentValue,
} from "../service/structuredFeeAdjustment";
import type { FulfillmentOrderLine } from "../types";
import { ConfirmationInput } from "./editor/primitives";
import { formatConfirmationDecimal } from "../service/confirmationDecimalDisplay";
import { confirmationBillingCountUnit } from "../service/confirmationEditorView";

type Props = {
  line: FulfillmentOrderLine;
  value: StructuredFeeAdjustmentValue;
  isNewService?: boolean;
  disabled?: boolean;
  onChange: (value: StructuredFeeAdjustmentValue) => void;
  formatAmount?: (value: string | null | undefined, signed?: boolean) => string;
};

/** 两条调整链路共用的结构化输入与服务差额预览。 */
export function StructuredFeeAdjustmentEditor({ line, value, onChange, isNewService = false, disabled = false, formatAmount }: Props) {
  const preview = previewStructuredFeeAdjustment(line, value);
  const specUnit = feeAdjustmentSpecUnit(line.resourceType);
  const displaySpecUnit = confirmationBillingCountUnit(line);
  const inputId = useId();
  const canEditCount = canEditFeeAdjustmentBillingCount(line.resourceType);
  const update = (patch: Partial<StructuredFeeAdjustmentValue>) => onChange({
    ...value,
    specUnit,
    ...patch,
  });
  const compact = formatAmount ? formatConfirmationDecimal : (text: string) => text.replace(/\.00$/u, "").replace(/(\.\d)0$/u, "$1");
  const money = formatAmount || formatFeeAdjustmentAmount;
  const before = {
    quantity: line.quantity, billing_count: line.billingCount, unit: line.unit,
    spec_unit: displaySpecUnit, pricing_unit_type: line.pricingUnitType,
    unit_price_amount: line.unitPriceAmount, amount: line.amount,
  };
  const summary = buildFeeAdjustmentDraftDisplay({
    draft_line_id: line.id, adjustment_type: isNewService ? "add_service" : "modify_existing", source_confirmation_line_id: isNewService ? null : line.id,
    item_name: null, resource_type: null, service_provider_type: null, service_provider_id: null,
    settlement_supplier_id: null, expected_before: isNewService ? null : before,
    after: { ...before, quantity: value.quantity, billing_count: value.billingCount,
      unit_price_amount: value.unitPriceAmount, amount: preview.normalized ? preview.amount : "" },
  }, line);
  return (
    <section className="confirmation-adjustment-editor" aria-label="结构化费用调整">
      {!isNewService && <section className="confirmation-adjustment-original" aria-label="原内容">
        <h3>原内容</h3>
        <dl>
          <div><dt>数量（{line.unit}）</dt><dd>{compact(line.quantity)}</dd></div>
          <div><dt>规格（{displaySpecUnit}）</dt><dd>{compact(line.billingCount)}</dd></div>
          <div><dt>单价（元）</dt><dd>{money(line.unitPriceAmount, false)}</dd></div>
          <div><dt>金额（元）</dt><dd>{money(line.amount, false)}</dd></div>
        </dl>
      </section>}
      <fieldset className="confirmation-adjustment-fields" disabled={disabled}>
        <legend>调整内容</legend>
        <div className="confirmation-adjustment-field-grid">
          <label htmlFor={`${inputId}-quantity`}>数量（{line.unit}）<ConfirmationInput id={`${inputId}-quantity`} aria-label="新数量" type="number" inputMode="decimal" value={value.quantity} min={0} step={1} disabled={disabled} onChange={(event) => update({ quantity: event.target.value })} /></label>
          <label htmlFor={`${inputId}-count`}>规格（{displaySpecUnit}）<ConfirmationInput id={`${inputId}-count`} aria-label="新规格数" type="number" inputMode="decimal" value={value.billingCount} disabled={disabled || !canEditCount} aria-describedby={!canEditCount ? `${inputId}-count-hint` : undefined} min={0} step={1} onChange={(event) => update({ billingCount: event.target.value })} /></label>
          <label htmlFor={`${inputId}-price`}>单价（元）<ConfirmationInput id={`${inputId}-price`} aria-label="新单价" type="number" inputMode="decimal" value={value.unitPriceAmount} min={0} step={0.01} disabled={disabled} onChange={(event) => update({ unitPriceAmount: event.target.value })} /></label>
          <div className="confirmation-adjustment-total"><span>金额（元）</span><output aria-live="polite">{preview.normalized ? money(preview.amount, false) : "¥ —"}</output></div>
        </div>
        {!canEditCount ? <p id={`${inputId}-count-hint`} className="confirmation-adjustment-hint">本类费用规格固定，保留原规格数；可调整数量和单价。</p> : null}
      </fieldset>
      <section className="confirmation-adjustment-result" aria-label="费用变化预览">
        <h3>调整结果</h3>
        <FeeAdjustmentServiceTable lines={[summary]} amountKind="cost" label="调整结果" groupDescriptionAmounts formatAmount={formatAmount} />
      </section>
      {preview.error && preview.error !== "数量、规格数和单价均未发生变化" ? <p className="confirmation-adjustment-error" role="alert">{preview.error}</p> : null}
    </section>
  );
}
